import { ChevronDownIcon, FontColorEnum, IconSizeEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useListbox } from './ListboxContext';
import ListboxItemRoot from './ListboxItemRoot';
import { ListboxDataItem, ListboxItemProps } from './ListboxTypes';

function ListboxItem({
  item,
  onSelect,
  className,
  dark,
  color = FontColorEnum.Neutral,
  isFirstOpen = false,
  isRoot = false,
}: ListboxItemProps & { isRoot?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { parentCanBeChecked } = useListbox();

  useEffect(() => {
    if (isRoot && isFirstOpen) {
      setIsOpen(true);
    }
  }, [isFirstOpen, isRoot]);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen(prev => !prev);
  };

  const formattedItems: ListboxDataItem[] = [];

  if (parentCanBeChecked && item.children && item.children.length > 0) {
    formattedItems.push({
      key: 'selectAll',
      label: t('Todas'),
      value: 'selectAll',
      childrenIds: item.children.map(child => child.value),
      isSelectAll: true,
    });
    for (let index = 0; index < item.children.length; index++) {
      formattedItems.push(item.children[index]);
    }
  } else {
    for (let index = 0; index < (item.children ?? []).length; index++) {
      if (item.children) formattedItems.push(item.children[index]);
    }
  }

  return (
    <div>
      <ListboxItemRoot
        dark={dark}
        color={color}
        item={item}
        onSelect={onSelect}
        beforeContentComponent={
          Boolean(item.children?.length) && (
            <button
              className="outline-none"
              onClick={handleToggle}
              aria-label={item.label}
              aria-expanded={isOpen}
            >
              <ChevronDownIcon
                size={IconSizeEnum.SM}
                className={clsx(
                  'mr-2 transition-transform duration-200',
                  {
                    '-rotate-90': !isOpen,
                  },
                  { 'text-text-light': dark }
                )}
              />
            </button>
          )
        }
        className={className}
      />
      {isOpen && Boolean(item.children?.length) && (
        <div className="mt-1 flex flex-col space-y-1 pl-3.5">
          {formattedItems.map((subitem, index) => (
            <div
              className={clsx('', {
                'pl-6': !subitem.children?.length,
              })}
              key={subitem.key}
            >
              <ListboxItem
                dark={dark}
                key={subitem.key}
                onSelect={onSelect}
                item={subitem}
                isFirstOpen={isFirstOpen && index === 0}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ListboxItem;
